/**
 * Admin Introductions Page
 * Manage introduction requests between sellers and buyers
 */

import { useState, useEffect } from 'react';
import { introductionService } from '../../services/introductionService';
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Building2,
  Mail,
  Calendar,
  MessageSquare,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  Send
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'yellow', icon: Clock },
  accepted: { label: 'Accepted', color: 'blue', icon: CheckCircle },
  declined: { label: 'Declined', color: 'red', icon: XCircle },
  completed: { label: 'Completed', color: 'green', icon: CheckCircle },
  expired: { label: 'Expired', color: 'gray', icon: AlertCircle }
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colorClasses[config.color]}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
}

function StatsCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200'
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </div>
  );
}

function IntroductionCard({ request, onAction, expanded, onToggle }) {
  const [message, setMessage] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleAction = async (action) => {
    setProcessing(true);
    try {
      if (action === 'complete') {
        await onAction(request.id, action, message, contactName, contactEmail);
      } else {
        await onAction(request.id, action, message);
      }
      setMessage('');
      setContactName('');
      setContactEmail('');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          {/* Connection visualization */}
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-purple-500" />
              <span className="font-medium">{request.seller_name || 'Unknown Seller'}</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-blue-500" />
              <span className="font-medium">{request.buyer_name || 'Unknown Buyer'}</span>
            </div>
          </div>

          <StatusBadge status={request.status} />

          <span className="text-xs text-gray-500">
            {request.request_type === 'seller_to_buyer' ? 'Seller initiated' : 'Buyer initiated'}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{formatDate(request.created_at)}</span>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-200 px-4 py-4 space-y-4">
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Seller</p>
              <p className="font-medium">{request.seller_name}</p>
              <p className="text-gray-600">{request.seller_email}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">Buyer</p>
              <p className="font-medium">{request.buyer_name}</p>
              <p className="text-gray-600">{request.buyer_email}</p>
            </div>
          </div>

          {/* Request message */}
          <div>
            <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              Request Message
            </p>
            <div className="bg-gray-50 rounded-lg p-3 text-sm whitespace-pre-wrap">
              {request.request_message}
            </div>
          </div>

          {/* Previous admin response if any */}
          {request.admin_response && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Previous Admin Response</p>
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                {request.admin_response}
              </div>
            </div>
          )}

          {/* Actions for pending requests */}
          {request.status === 'pending' && (
            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a message to include in the notification..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleAction('accept')}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Accept
                </button>
                <button
                  onClick={() => handleAction('decline')}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Decline
                </button>
              </div>
            </div>
          )}

          {/* Actions for accepted requests - mark as complete */}
          {request.status === 'accepted' && (
            <div className="space-y-3 pt-2">
              <p className="text-sm font-medium text-gray-700">Complete Introduction</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Target Contact Name</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="e.g., John Smith"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Target Contact Email</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="e.g., john@company.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Next Steps Message (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Any next steps or additional information..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <button
                onClick={() => handleAction('complete')}
                disabled={processing}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Mark as Completed & Notify
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminIntroductions() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [requestsData, statsData] = await Promise.all([
        introductionService.getAllRequests({ status: filter === 'all' ? null : filter }),
        introductionService.getStats()
      ]);
      setRequests(requestsData.requests || []);
      setStats(statsData.stats || null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filter]);

  const handleAction = async (requestId, action, message, contactName, contactEmail) => {
    try {
      if (action === 'accept') {
        await introductionService.acceptRequest(requestId, message);
      } else if (action === 'decline') {
        await introductionService.declineRequest(requestId, message);
      } else if (action === 'complete') {
        await introductionService.completeRequest(requestId, {
          adminMessage: message,
          targetContactName: contactName,
          targetContactEmail: contactEmail
        });
      }
      await loadData();
      setExpandedId(null);
    } catch (err) {
      console.error('Error processing action:', err);
      alert(err.message);
    }
  };

  if (loading && !requests.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading introduction requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-purple-600" />
              Introduction Requests
            </h1>
            <p className="text-gray-600 mt-1">Manage introduction requests between sellers and buyers</p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <StatsCard label="Pending" value={stats.pending} icon={Clock} color="yellow" />
            <StatsCard label="Accepted" value={stats.accepted} icon={CheckCircle} color="blue" />
            <StatsCard label="Completed" value={stats.completed} icon={CheckCircle} color="green" />
            <StatsCard label="Declined" value={stats.declined} icon={XCircle} color="red" />
            <StatsCard label="Total" value={stats.total} icon={Users} color="gray" />
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">Filter:</span>
          <div className="flex gap-2">
            {['all', 'pending', 'accepted', 'completed', 'declined'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1.5 text-sm rounded-lg ${
                  filter === status
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Request list */}
        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No introduction requests found</p>
            </div>
          ) : (
            requests.map((request) => (
              <IntroductionCard
                key={request.id}
                request={request}
                expanded={expandedId === request.id}
                onToggle={() => setExpandedId(expandedId === request.id ? null : request.id)}
                onAction={handleAction}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
